import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasChunkError: boolean;
}

/**
 * ChunkErrorBoundary
 *
 * Catches dynamic-import / chunk-load failures that happen when a user's
 * cached page references JS files from a previous Vercel deployment.
 *
 * On detection it shows a brief "Updating…" screen and auto-reloads once,
 * using sessionStorage to prevent infinite loops.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State | null {
    if (isChunkLoadError(error)) {
      return { hasChunkError: true };
    }
    // Not a chunk error — let it bubble to the Sentry boundary
    return null;
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error)) {
      this.handleChunkError();
    }
  }

  handleChunkError() {
    const key = 'chunk-reload-ts';
    const lastReload = sessionStorage.getItem(key);
    const now = Date.now();

    // Only auto-reload if we haven't done so in the last 10 seconds
    if (!lastReload || now - Number(lastReload) > 10_000) {
      sessionStorage.setItem(key, String(now));
      window.location.reload();
    }
    // If we already reloaded recently, the fallback UI stays visible
    // so the user can manually refresh or navigate.
  }

  render() {
    if (this.state.hasChunkError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="max-w-sm w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Updating to latest version…
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              A new version has been deployed. The page will refresh automatically.
            </p>
            <button
              onClick={() => {
                sessionStorage.removeItem('chunk-reload-ts');
                window.location.reload();
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Refresh Now
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/** Detect chunk / dynamic-import load errors by their error message. */
function isChunkLoadError(error: Error): boolean {
  const msg = error.message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('Unable to preload CSS') ||
    msg.includes("Importing a module script failed") ||
    // Vite-specific: when the preloaded asset returns HTML instead of JS
    msg.includes('is not a valid JavaScript MIME type')
  );
}
